import { type BaseSyntheticEvent, type ReactNode, startTransition } from 'react';
import { type FieldValues, FormProvider, type UseFormReturn } from 'react-hook-form';

/**
 * Submit handler for `<Form>`. Strictly narrower than react-hook-form's own
 * `SubmitHandler<T>` (which allows a synchronous `unknown` return): our variant
 * requires the handler to return a `Promise` so `formState.isSubmitting` stays
 * `true` for the full duration of any awaited work (mutation, side effects). This
 * is what lets `<FormSubmitButton>` derive its pending state from context.
 *
 * `mutation.mutate` returns `void` and does not satisfy this contract — use
 * `await mutation.mutateAsync(...)` (or an inline `async` handler) instead.
 */
export type FormSubmitHandler<T = FieldValues> = (data: T, event?: BaseSyntheticEvent) => Promise<unknown>;

// `TTransformedValues` mirrors react-hook-form's third generic: when the resolver's schema transforms
// (e.g. date segments → `Date`), the submit handler receives the transformed *output*, not the raw
// field values. It defaults to `TFieldValues`, so forms without a transform are unaffected.
export type FormProps<TFieldValues extends FieldValues = FieldValues, TContext = unknown, TTransformedValues = TFieldValues> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  children: ReactNode;
  className?: string;
} & (
  | { onSubmit: FormSubmitHandler<TTransformedValues>; action?: never }
  | { action: (data: TTransformedValues) => void; onSubmit?: never }
);

export function Form<TFieldValues extends FieldValues = FieldValues, TContext = unknown, TTransformedValues = TFieldValues>({
  form,
  children,
  className,
  onSubmit,
  action,
}: FormProps<TFieldValues, TContext, TTransformedValues>) {
  // RHF's handleSubmit rethrows any error thrown from onSubmit
  // (see: https://react-hook-form.com/docs/useform/handlesubmit).
  const handleSubmit = onSubmit
    ? async (e?: BaseSyntheticEvent) => {
        try {
          await form.handleSubmit(onSubmit)(e);
        } catch (err) {
          if (import.meta.env.DEV) {
            // biome-ignore lint/suspicious/noConsole: dev-only surfacing of the RHF rethrow so bugs in the onSubmit body aren't invisible.
            console.error('Form submit rejection:', err);
          }
        }
      }
    : action
      ? form.handleSubmit((data) => startTransition(() => action(data)))
      : undefined;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className={className} noValidate>
        {children}
      </form>
    </FormProvider>
  );
}
